import { useEffect, useState } from "react"

import { Alert, Flex, Segmented, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Button from "../../../../../../Components/Button"
import InputNumber from "../../../../../../Components/Input/InputNumber"
import Modal from "../../../../../../Components/Modal"
import Icon from "../../../../../../Icon"

import styles from "./autoSubmitModal.module.less"

const { Text } = Typography

// Auto-submission setup: how Recover auto-submits evidence. Mirrors the Alerts
// auto-refund modal (Segmented switcher + all-option card / threshold input) but adds
// the "wait N days" control (merged in from the old Submission-delay section) and the
// above-threshold safety note. Controlled via `initial` in / `onSave` out.
const AutoSubmitModal = ({ visible, onClose, onSave, initial }) => {
  const { t } = useTranslation()
  // The shared Modal switches to its bottom-sheet layout via this PROP (not redux):
  // without it, centered rendering collides with the mobile sheet CSS and the whole
  // popup lands off-screen at phone widths.
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [mode, setMode] = useState("all")
  const [amount, setAmount] = useState(250)
  const [delayDays, setDelayDays] = useState(2)

  // Reset local state from the saved values every time the modal opens.
  useEffect(() => {
    if (!visible) return
    setMode(initial?.mode === "threshold" ? "threshold" : "all")
    setAmount(Number(initial?.amount ?? 250) || 0)
    setDelayDays(Number(initial?.delayDays ?? 2) || 0)
  }, [visible, initial])

  const handleConfirm = () => {
    onSave({ mode, amount: Number(amount) || 0, delayDays: Number(delayDays) || 0 })
  }

  // Shared send-timing control (used by both modes). The value is measured BACK from the
  // issuer's deadline, matching the summary line on the Preferences page ("Evidence is
  // sent N days before the deadline"), so the two never contradict each other.
  const waitDaysBlock = (
    <Flex vertical gap={8}>
      <Text strong>{t("Send this many days before the deadline")}</Text>
      <InputNumber
        addonAfter={<Text type="secondary">{t("Days")}</Text>}
        min={0}
        max={30}
        step={1}
        precision={0}
        value={delayDays}
        onChange={(v) => setDelayDays(v ?? 0)}
        style={{ width: "100%" }}
      />
      <Text type="secondary">
        {t(
          "Your team can review or edit the evidence up until then. If a dispute arrives with less time left than this, we submit it straight away so nothing lapses."
        )}
      </Text>
    </Flex>
  )

  // Same modal shell as the Alerts Auto-refund setup (AutoRefundPage.js): clear-bg
  // content, 442 width, description under the title, Cancel/Confirm inside the body.
  return (
    <Modal
      visible={visible}
      isMobile={isMobile}
      handleClose={onClose}
      title={t("Auto-submission setup")}
      footer={null}
      destroyOnClose
      width={442}
      closeIcon={<Icon type="cross_small" />}
      classNames={{ content: "clear-bg" }}
      className="change-refund-form-modal"
    >
      <Text type="secondary">
        {t("We submit your evidence automatically before each deadline.")}
      </Text>
      <Flex vertical gap={16} className={styles.root} style={{ marginTop: 16 }}>
        <Segmented
          block
          className={styles.switcher}
          value={mode}
          onChange={setMode}
          options={[
            { label: t("Auto-submit all disputes"), value: "all" },
            { label: t("Set custom threshold"), value: "threshold" }
          ]}
        />

        {mode === "all" ? (
          <>
            <Flex vertical gap={8} align="center" justify="center" className={styles.card}>
              <Icon type="circle_check_green" className={styles.cardIcon} />
              <Text strong>{t("All disputes are auto-submitted")}</Text>
            </Flex>
            <Text type="secondary">
              {t(
                "Every dispute is submitted automatically, no matter the amount. Choose how far ahead of the deadline we send it."
              )}
            </Text>
            {waitDaysBlock}
          </>
        ) : (
          <>
            <Flex vertical gap={8}>
              <Text strong>{t("Auto-submit disputes up to")}</Text>
              <InputNumber
                addonBefore={<Text type="secondary">$</Text>}
                min={0}
                step={10}
                precision={2}
                value={amount}
                onChange={(v) => setAmount(v ?? 0)}
                style={{ width: "100%" }}
              />
            </Flex>
            {waitDaysBlock}
            <Alert
              type="warning"
              showIcon
              icon={<Icon type="alert_triangle_error" style={{ fontSize: 20 }} />}
              message={<Text strong>{t("Larger disputes are never missed")}</Text>}
              description={
                <Text>
                  {t(
                    "Disputes above this amount wait for your review, but we still auto-submit them on the last day before the deadline so none slip past."
                  )}
                </Text>
              }
            />
            <Text type="secondary">
              {t(
                "Need to hold specific disputes back entirely? Create a rule in Auto-submission exceptions."
              )}
            </Text>
          </>
        )}
      </Flex>

      <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 0", marginTop: 24 }}>
        <Button size="large" htmlType="button" onClick={onClose} text={t("Cancel")} />
        <Button size="large" type="primary" onClick={handleConfirm} text={t("Confirm")} />
      </Flex>
    </Modal>
  )
}

export default AutoSubmitModal
